import { Suspense, lazy, useEffect } from "react";
import { X } from "lucide-react";
import BrandSpinner from "@/components/ui/BrandSpinner";
import { GenieMist } from "@/pages/agent-interfaces/live-agent/components/GenieMist";
import type { HelpHubBranding } from "@/types/help-hub/help-hub";
import { hubBrandTheme } from "../brandInk";

/**
 * Loaded only when a visitor actually opens the Genie. The live-agent
 * experience pulls in the whole voice stack, and most people who land on a
 * Genie Page read a guide and leave.
 */
const AgentStudioPage = lazy(() => import("@/pages/genie/studio/AgentStudioPage"));

interface HubGenieStageProps {
  /** Business name shown on the stage frame. */
  title: string;
  branding: HelpHubBranding;
  /** Sent as the first message. Undefined opens on the normal start screen. */
  question?: string;
  onClose: () => void;
}

/**
 * The immersive Genie stage — a full-screen takeover over a dark, brand-tinted
 * backdrop.
 *
 * This replaces the v1 `<iframe src="/embed/...">`, which was wrong twice over:
 * it served the legacy widget UI rather than the live-agent experience, and an
 * iframe can neither inherit the page's branding nor be handed the question the
 * visitor just typed.
 *
 * Nothing about the conversation is reimplemented here. `AgentStudioPage` reads
 * the same `:url_name` route param the hub is already mounted on, so passing
 * `portalMode` (which drops its own back/share chrome) gives the complete
 * experience — orb, transcript, resources rail, wrap-up, Inbox landing — for
 * the cost of a frame around it. Same reuse the public portal already relies on.
 */
export const HubGenieStage = ({ title, branding, question, onClose }: HubGenieStageProps) => {
  // Escape closes, and the page behind must not scroll under the takeover.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      // Nested Radix menus/sheets consume Escape before it reaches window.
      // Dismissing those controls must not tear down the active conversation.
      if (event.key === "Escape" && !event.defaultPrevented) onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener("keydown", onKeyDown);
    };
  }, [onClose]);

  const theme = hubBrandTheme(branding);
  const accent = theme.secondary;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Talk to ${title}`}
      className="fixed inset-0 z-[3000] flex flex-col"
      style={{
        background: `radial-gradient(120% 90% at 20% 0%, ${branding.primaryColor}59 0%, transparent 60%),
                     radial-gradient(120% 90% at 85% 100%, ${accent}4D 0%, transparent 62%),
                     #0B1020`,
      }}
    >
      <GenieMist brandColor={branding.primaryColor} secondaryColor={branding.secondaryColor} intensity={0.7} />

      {/* Minimal stage chrome — just the way out. The conversation sheet below
          carries the genie's identity (mark, name, live status), so repeating a
          name band up here read as two stacked headers and wasted the stage. */}
      <header className="relative z-10 flex items-center justify-end px-4 py-2.5 sm:px-6">
        <button
          type="button"
          onClick={onClose}
          aria-label="Close and go back"
          className="flex h-9 w-9 items-center justify-center rounded-full bg-white/15 text-white transition-colors hover:bg-white/30"
        >
          <X className="h-4 w-4" strokeWidth={2.5} />
        </button>
      </header>

      <div className="relative z-10 min-h-0 flex-1 px-2 pb-2 sm:px-4 sm:pb-4">
        {/*
          `AgentStudioPage`'s root carries an inline `min-height: 100dvh`, which
          would push it out of this framed card. An `!important` utility is the
          only thing that outranks an inline style, and it keeps the override
          here rather than adding a stage-only prop to the page.
        */}
        <div className="h-full w-full overflow-hidden rounded-2xl bg-[#F9FAFC] shadow-2xl ring-1 ring-white/10 [&>div]:!h-full [&>div]:!min-h-full">
          <Suspense fallback={<BrandSpinner label="Waking the Genie…" className="min-h-full" />}>
            <AgentStudioPage portalMode initialQuestion={question} />
          </Suspense>
        </div>
      </div>
    </div>
  );
};

export default HubGenieStage;
